// AeroKnobCluster — left-side FAB cluster on the map.
//
// Replaces the old right-side record button and adds a new Aero knob
// that expands a quick-pop strip of aeronautical-layer toggles.
//
// Layout (absolute, left edge, bottom = above instrument strip):
//
//   [REC]   <- Track-log record / stop (play triangle <-> filled square)
//   [AERO]  <- Aero knob (sliders icon, mixer-board look)
//
// When the Aero knob is tapped, a horizontal quick-pop strip slides
// in to the right of the cluster. Each chip is the SAME h-11 w-11
// size as the knobs (boss req 2026-05-13: 'only SVG icons and no
// text, so the size can match the little airplane button'). Active
// chips get a tinted border + glow; inactive sit dim. Hover-tooltips
// (title=) + aria-label still carry the human label so the icon set
// stays scannable and accessible.
//
//   [APT] [NAV] [FIX] [HELI] [B/C/D] [AWY] [...]
//
// The [...] button opens AeroSettingsSheet (full bottom sheet with
// per-class sub-options).

import { useState, useEffect, useRef } from 'react';
import {
  // Cluster trigger + record states
  SlidersVertical, Play, Square,
  // Heliport chip keeps the lucide glyph — no clean FAA symbology
  // fit at 16 px, and the lucide helicopter glyph is recognizable.
  Helicopter,
  // Deep-settings affordance
  MoreHorizontal,
} from '../shell/Icon';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useTrackStore } from '../stores/trackStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';
import type { LayerId } from './layerCatalog';
import AeroSettingsSheet from './AeroSettingsSheet';

// ── Hand-drawn stencil icons ────────────────────────────────
//
// Three custom SVG glyphs that match the on-map FAA symbology so each
// chip previews what the layer paints. Sized for h-11 w-11 chips:
// 18x18 viewBox, stroke-only at currentColor so tailwind text-* utility
// classes still drive the active/inactive color through React inheritance.
//
//   AirportStencil   ring + N/S/E/W ticks (FAA sectional towered/fuel)
//   NavaidStencil    upward triangle (compass-rose / VOR pyramid)
//   AirspaceStencil  three concentric circles (Class B/C/D stack)

function AirportStencil({ size = 18 }: { size?: number }) {
  // Matches src/map/airportIcons.ts svgRing() but pared down to a
  // single-color, currentColor stroke since the chip's tint is
  // already applied via the button's text- color class.
  return (
    <svg
      width={size} height={size} viewBox="0 0 18 18"
      fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="4" />
      <line x1="9" y1="1.5" x2="9" y2="4" />
      <line x1="9" y1="14"  x2="9" y2="16.5" />
      <line x1="1.5" y1="9" x2="4"  y2="9" />
      <line x1="14"  y1="9" x2="16.5" y2="9" />
    </svg>
  );
}

function NavaidStencil({ size = 18 }: { size?: number }) {
  // FAA sectional VOR / VORTAC convention: flat-top hexagon ("compass
  // rose") with a center station dot. Three small antenna pegs at
  // alternating vertices (the TACAN triplet on a VORTAC) push the
  // glyph past plain VOR — a single chip should read as the broader
  // category 'navaid' to anyone who's scanned a sectional. Mirrors
  // the on-map symbology in src/map/navaidIcons.ts so the chip is a
  // miniature preview of what the toggle paints.
  //
  // Geometry: flat-top hexagon = vertices at 0°, 60°, 120°, 180°,
  // 240°, 300°. Hardcoded at the 6 points below so the React tree
  // stays cheap (no useMemo / Math.* in render).
  return (
    <svg
      width={size} height={size} viewBox="0 0 18 18"
      fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinejoin="round" strokeLinecap="round"
      aria-hidden="true"
    >
      {/* TACAN pegs projecting from the right, upper-left, and
          lower-left vertices (alternating triplet, matching the
          on-map VORTAC icon in navaidIcons.ts). Drawn FIRST so the
          hexagon stroke overlays the inboard ends — they read as
          stubs poking outward.
            right       vertex (15, 9)   -> (17, 9)
            upper-left  vertex (6, 3.8)  -> (5, 2.1)
            lower-left  vertex (6, 14.2) -> (5, 15.9) */}
      <line x1="15"   y1="9"     x2="17"  y2="9" />
      <line x1="6"    y1="3.8"   x2="5"   y2="2.1" />
      <line x1="6"    y1="14.2"  x2="5"   y2="15.9" />
      {/* Flat-top hexagon — a flat top and flat bottom, two angled
          edges on each side. */}
      <polygon points="15,9 12,14.2 6,14.2 3,9 6,3.8 12,3.8" />
      {/* Center station dot — filled, matches FAA convention. */}
      <circle cx="9" cy="9" r="1.0" fill="currentColor" stroke="none" />
    </svg>
  );
}

function AirwayStencil({ size = 16 }: { size?: number }) {
  // Airways are point-to-point segments between fixes / navaids —
  // STRAIGHT lines, not the smooth curve lucide's Route glyph drew.
  // Boss callouts 2026-05-13:
  //   #1: 'make the airways look like waypoints with straight lines
  //        between instead of the curvy line stencil, which is
  //        reserved for our flight plan in our styling'.
  //   #2: 'the airways icon looks too much like a V. Could you make
  //        it look more like airways, with like a few connecting
  //        straight lines with dots on the end that look random?'
  //
  // Revised stencil: four waypoint dots scattered across the chip
  // at irregular positions, connected by three straight segments
  // forming an asymmetric polyline. Reads as 'a tiny excerpt of an
  // airway network' rather than a symmetric V. The path zig-zags
  // at non-mirrored angles so the eye doesn't snap to any letter
  // shape.
  //
  // Layout (18x18 viewBox):
  //     . . . . . . . . . . . .
  //     .   .   D2  .   .   .
  //     .   .  / \  .   .   .
  //     . D1   .  \ . D4   .
  //     .   \  .   .   .   .
  //     .    \ .   .  .    .
  //     .   . D3  .   .   .
  //  where D1=(3,7), D2=(8,3), D3=(11,13), D4=(15.5,8.5)
  //  with segments D1->D2, D2->D3, D3->D4.
  return (
    <svg
      width={size} height={size} viewBox="0 0 18 18"
      fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Asymmetric polyline through four scattered fix points. The
          three segment angles are intentionally irregular (no two
          segments share a slope) so the glyph reads as 'a network
          fragment' rather than a chevron or letter. */}
      <polyline points="3,7 8,3 11,13 15.5,8.5" />
      {/* Waypoint dots — filled. Matches the fix-stencil's filled
          center dot so the chip stays visually consistent with the
          rest of the aero family. */}
      <circle cx="3"    cy="7"    r="1.2" fill="currentColor" stroke="none" />
      <circle cx="8"    cy="3"    r="1.2" fill="currentColor" stroke="none" />
      <circle cx="11"   cy="13"   r="1.2" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="8.5"  r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function FixStencil({ size = 14 }: { size?: number }) {
  // Named IFR enroute fixes (5-letter waypoints) are rendered as
  // upward triangles on FAA IFR enroute charts. Drawn slightly
  // smaller than the navaid hex so the two chips stay visually
  // distinct in the strip.
  return (
    <svg
      width={size} height={size} viewBox="0 0 18 18"
      fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="9,2.5 16,15 2,15" />
      <circle cx="9" cy="11" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function AirspaceStencil({ size = 18 }: { size?: number }) {
  // Three concentric circles — reads as 'stacked airspace floors'
  // which is exactly how Class B/C/D inverted-wedding-cake shelves
  // look from above on the sectional.
  return (
    <svg
      width={size} height={size} viewBox="0 0 18 18"
      fill="none" stroke="currentColor" strokeWidth="1.4"
      aria-hidden="true"
    >
      <circle cx="9" cy="9" r="7.2" />
      <circle cx="9" cy="9" r="4.6" />
      <circle cx="9" cy="9" r="2" />
    </svg>
  );
}

// ── Aero quick-pop chip definitions ──────────────────────────────────
//
// Icon-only chips so each one renders at h-11 w-11 (same footprint
// as the trigger knob). Label is preserved as the title= tooltip and
// the aria-label so screen readers and hover-cursor users still get
// the text.
//
// Icon picks:
//   APT    Plane      — FAA sectional uses circle/airplane for airports
//   NAV    Radio      — VOR / VORTAC are radio-nav stations
//   FIX    Hexagon    — enroute fixes carry hex/triangle symbology
//                        on FAA IFR charts
//   HELI   Helicopter — lucide ships a proper helicopter glyph
//   B/C/D  Layers     — stacked airspace floors
//   AWY    Route      — a routed line; no need to spell out V vs J
//                        in 36 px (color in the AeroSettingsSheet
//                        handles disambiguation when needed)
interface AeroChip {
  id: LayerId;
  /** Human label — used for title tooltip + aria-label, NOT rendered. */
  label: string;
  icon: React.ReactNode;
  /** Border + bg classes applied when this layer is active. */
  activeColor: string;
}

const AERO_CHIPS: AeroChip[] = [
  {
    id: 'airports',
    label: 'Airports',
    icon: <AirportStencil size={16} />,
    // Amber tint. Boss req 2026-05-13: chip was the only one with no
    // real color and read as washed-out white. Magenta would have
    // matched the FAA sectional convention for non-towered airports
    // but it collides hard with the heliport chip's pink right next
    // door. Amber stands clearly apart from every other tint in the
    // strip (cyan navaids / blue fixes / pink heliports / blue B-C-D
    // / purple airways), and aligns with the warm 'airport callout'
    // accent ForeFlight + Garmin Pilot both use for airport info.
    activeColor: 'border-amber-400 bg-amber-900/40',
  },
  {
    id: 'waypoints',
    label: 'Navaids',
    icon: <NavaidStencil size={18} />,
    activeColor: 'border-cyan-500 bg-cyan-900/40',
  },
  {
    id: 'fixes',
    label: 'Fixes',
    // Upward triangle stencil — FAA IFR enroute charts mark named
    // fixes (5-letter waypoints) as triangles. Visually distinct
    // from the VORTAC hexagon used for navaids so the two adjacent
    // chips don't blur together.
    icon: <FixStencil size={14} />,
    activeColor: 'border-blue-400 bg-blue-900/40',
  },
  {
    id: 'heliports',
    label: 'Heliports',
    icon: <Helicopter size={16} />,
    activeColor: 'border-pink-400 bg-pink-900/40',
  },
  {
    id: 'classBCD',
    label: 'Class B / C / D',
    icon: <AirspaceStencil size={16} />,
    activeColor: 'border-blue-500 bg-blue-900/40',
  },
  {
    id: 'airways',
    label: 'Airways',
    icon: <AirwayStencil size={16} />,
    activeColor: 'border-purple-400 bg-purple-900/40',
  },
];

// ── Component ─────────────────────────────────────────────────────────
export default function AeroKnobCluster() {
  const [aeroOpen, setAeroOpen] = useState(false);
  const [deepOpen, setDeepOpen] = useState(false);

  const recording  = useTrackStore((s) => s.recording);
  const startTrack = useTrackStore((s) => s.start);
  const stopTrack  = useTrackStore((s) => s.stop);

  const toggleLayer = useLayerVisibilityStore((s) => s.toggleLayer);
  const layers      = useLayerVisibilityStore((s) => s.layers);

  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const instrumentH = useInstrumentStripHeight(showInstruments);

  // Elapsed-time counter shown on the Record button while recording.
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    if (recording) {
      startRef.current = Date.now();
      const id = window.setInterval(() => {
        setElapsed(Math.floor((Date.now() - (startRef.current ?? Date.now())) / 1000));
      }, 1000);
      return () => { window.clearInterval(id); startRef.current = null; };
    } else {
      setElapsed(0);
    }
  }, [recording]);

  // Close quick-pop when Aero knob is toggled off.
  const handleAeroToggle = () => {
    setAeroOpen((v) => !v);
    if (deepOpen) setDeepOpen(false);
  };

  // Cluster sits above the instrument bar (same anchor as right-rail FABs).
  const bottomOffset = instrumentH > 0 ? `calc(${instrumentH}px + 0.75rem)` : '11rem';

  return (
    <>
      {/* ── Knob cluster ── */}
      {/* `data-fy-aero-cluster` lets PlateOverlayControls measure this
          cluster's rendered height so its own card stacks above it
          without overlap (boss bug 2026-05-15: opacity card was
          crowding the Aero knob). */}
      <div
        data-fy-aero-cluster
        className="absolute z-20 flex flex-col gap-2 items-start"
        style={{ bottom: bottomOffset, left: 'calc(var(--safe-left, 0px) + 0.75rem)' }}
      >
        {/* Record / Stop track — play triangle when idle, filled square
            when recording (mirrors Garmin G3X / Avidyne / iOS Voice
            Memos convention). The Mic glyph that used to live here was
            a leftover from when this was sketched as a 'mic record'
            affordance and confused pilots expecting transport controls. */}
        <button
          onClick={() => (recording ? stopTrack() : startTrack())}
          className={[
            'border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur transition-colors',
            recording
              ? 'bg-red-600/90 border-red-500 text-white animate-pulse'
              : 'bg-cockpit-800/90 border-cockpit-600',
          ].join(' ')}
          aria-label={recording ? 'Stop track' : 'Start track log'}
          title={recording ? `Stop track log (${fmtElapsed(elapsed)})` : 'Start track log'}
        >
          {recording ? <Square size={16} /> : <Play size={16} />}
        </button>

        {/* Aero knob — mixer-board sliders icon. Boss req 2026-05-13:
            an icon that 'looks like slider knobs or buttons or
            something' instead of the previous little airplane. */}
        <button
          onClick={handleAeroToggle}
          className={[
            'border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur transition-colors',
            aeroOpen
              ? 'bg-ember-600/90 border-ember-400 text-white'
              : 'bg-cockpit-800/90 border-cockpit-600',
          ].join(' ')}
          aria-label={aeroOpen ? 'Close aero layers' : 'Aero layers'}
          title="Aeronautical layers"
        >
          <SlidersVertical size={16} />
        </button>

        {/* Quick-pop strip — slides right of the cluster. Each chip
            is h-11 w-11 with a 16 px inner icon so it matches the
            trigger knobs EXACTLY in both outer footprint and inner
            glyph weight (boss followup 2026-05-13: 'And make the
            buttons the same size as the menu knob button. Same
            dimensions').

            Wrapper has no padding so the row of chips sits at the
            same vertical line as the knobs in the column; gap-2
            still gives the chip spacing parity with the knob stack. */}
        {aeroOpen && (
          <div
            className="absolute left-full ml-2 bottom-0 flex items-center gap-2"
            role="toolbar"
            aria-label="Aero layer toggles"
          >
            {AERO_CHIPS.map((chip) => (
              <button
                key={chip.id}
                onClick={() => toggleLayer(chip.id)}
                className={[
                  // Same h-11 w-11 + backdrop-blur as the knobs above
                  // so the row of chips reads as a horizontal
                  // continuation of the cluster, not a separate pill.
                  'border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur transition-colors',
                  layers[chip.id]
                    ? chip.activeColor + ' text-cockpit-100'
                    : 'border-cockpit-600 bg-cockpit-800/90 text-cockpit-300',
                ].join(' ')}
                aria-pressed={layers[chip.id]}
                aria-label={chip.label}
                title={chip.label}
              >
                {chip.icon}
              </button>
            ))}

            {/* Deep-settings button — same h-11 w-11 footprint as the
                chips so the strip is a uniform row of identical
                squares. Tap opens the full bottom-sheet of per-layer
                sub-options (hospital-only heliports, fuel filter, etc.). */}
            <button
              onClick={() => setDeepOpen(true)}
              className="border rounded-lg h-11 w-11 flex items-center justify-center backdrop-blur transition-colors border-cockpit-600 bg-cockpit-800/90 text-cockpit-300 hover:text-cockpit-100"
              title="More aero settings"
              aria-label="More aero settings"
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        )}
      </div>

      {/* ── Deep settings sheet ── */}
      {deepOpen && (
        <AeroSettingsSheet onClose={() => setDeepOpen(false)} />
      )}
    </>
  );
}

/** Format elapsed seconds as m:ss or h:mm:ss. */
function fmtElapsed(totalSec: number): string {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}
